import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Form,
  Input,
  Container,
  Row,
  Col,
  Spinner
} from "reactstrap";
import UserHeader from "components/Headers/UserHeader.js";
import { useAuth0 } from '@auth0/auth0-react';
import './Profile.css'; // Ensure this is imported to apply styles
import config from '../config';
import AuthFooter from "../components/Footers/AuthFooter"
import UsernameModal from "../layouts/UsernameModal.js";
import { useLocation } from "react-router-dom";


const Profile = () => {
  const { user, getIdTokenClaims, getAccessTokenSilently, isLoading, isAuthenticated, loginWithRedirect } = useAuth0();
  const [idTokenClaims, setIdTokenClaims] = useState(null);
  const [stats, setStats] = useState(null); // State for stats data
  const [error, setError] = useState(null); // Add error state
  const [username, setUsername] = useState(null); // Add error state
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const fetchIdTokenClaims = async () => {
      const claims = await getIdTokenClaims();
      setIdTokenClaims(claims);
    };

    if (!isLoading && isAuthenticated) {
      fetchIdTokenClaims();
    }
  }, [getIdTokenClaims, isLoading, isAuthenticated]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = await getAccessTokenSilently();
        const email = user.email;
        const apiUrl = `${config.apiUrl}/${config.apiStage}/userstats`; // Updated API route

        const response = await fetch(apiUrl, {
          method: 'GET', // Assuming the API uses POST; change to GET if needed
          headers: {
            Authorization: `Bearer ${token}`,
          },
     
        });

        if (!response.ok) {
          throw new Error(`Network response was not ok. Status: ${response.status}`);
        }

        const data = await response.json();
        setStats({
          total_quills: data.total_quills,
          active_quills: data.active_quills,
          total_quill_seen_count: data.total_quill_seen_count,
          max_quills: data.max_quills,
        });

      } catch (error) {
        console.error('Error fetching stats:', error);
        setError(error.message); // Set error message

      }
    };

    if (!isLoading && isAuthenticated && user) {
      fetchStats();
    }
  }, [getAccessTokenSilently, user, isAuthenticated, isLoading]);

  // Fetch username from Auth0
  useEffect(() => {
    const fetchUsername = async () => {
      try {
        const token = await getAccessTokenSilently();
        const apiUrl = `${config.apiUrl}/${config.apiStage}/user`; // Assuming the endpoint doesn't need user.sub in the URL
  
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
  
        // If the response status is not OK, handle the error.
        if (!response.ok) {
          throw new Error(`Network response was not ok. Status: ${response.status}`);
        }
  
        const data = await response.json();
  
        // Check the response structure based on the new API format
        if (data.status === 'success') {
          if (data.username) {
            setUsername(data.username); // Set the username if found
          } else {
            setUsername(' '); // Handle missing username
          }
        } else {
          // Handle unexpected response format or errors from the API
          console.log('Error fetching username');
        }
      } catch (error) {
        console.error('Error fetching username:', error);
        setUsername('Error fetching username'); // Handle any fetch errors
      }
    };
  
    if (!isLoading && isAuthenticated && user) {
      fetchUsername();
    }
  }, [getAccessTokenSilently, user, isAuthenticated, isLoading]);
  

  if (isLoading || !user || !stats) {
    return (
      <>
          <div className="header bg-gradient-info pb-8 pt-7 pt-md-8">
            <Container className="d-flex align-items-center justify-content-center" style={{ height: '50vh' }}>
              <Row>
              <Col className="text-center ">
                <Spinner color="info" />  {/* 'info' is lighter than 'primary' */}
                <div className="text-muted">Loading...</div>
              </Col>
              </Row>
            </Container>
          </div>
      </>
    );
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <>
      <UserHeader />
      {/* Page content */}
      <Container className="mt--7" fluid>
        <Row>
          <Col className="order-xl-2 mb-5 mb-xl-0" xl="4">
            <Card className="card-profile shadow">
              <Row className="justify-content-center">
                <Col className="order-lg-2" lg="3">
                  <div className="card-profile-image">
                    <a href="#pablo" onClick={(e) => e.preventDefault()}>
                      <img
                        alt="..."
                        src={user.picture}
                      />
                    </a>
                  </div>
                </Col>
              </Row>
              
              <CardBody className="pt-0 pt-md-4">
                <Row className="justify-content-center">
                  <h3 className="mt-4 d-flex justify-content-center mt-md-5 text-black">Stats & Settings</h3>
                </Row>
                <Row>
                  <div className="col text-black">
                    <div className="card-profile-stats d-flex justify-content-center mt-md-0">
                      <div>
                        <span className="heading">{stats.active_quills}</span>
                        <span className="description">Active Quills</span>
                      </div>
                      <div>
                        <span className="heading">{stats.total_quills}</span>
                        <span className="description">Total Quills</span>
                      </div>
                      <div>
                        <span className="heading">{stats.max_quills}</span>
                        <span className="description">Max Quills</span>
                      </div>
                      <div>
                        <span className="heading">{stats.total_quill_seen_count}</span>
                        <span className="description">Seen Count</span>
                      </div>
                    </div>
                  </div>
                </Row>
              </CardBody>
            </Card>
          </Col>
          <Col className="order-xl-1" xl="8">
            <Card className="bg-secondary shadow">
              <CardHeader className="bg-white border-0">
                <Row className="align-items-center">
                  <Col xs="8">
                    <h3 className="mb-0 text-black">My Account</h3>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                <Form>
                  <h6 className="heading-small text-muted mb-4 ">
                    User information
                  </h6>
                  <div className="pl-lg-4">
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-username"
                          >
                            Username
                          </label>
                          <div className="d-flex align-items-center">
                            {/* If username is loading, show spinner */}
                            {username === null ? (
                              <Spinner color="primary" size="sm" className="mr-2" />
                            ) : (
                              <Input
                                className="form-control-alternative mr-2"
                                value={username || ""}
                                id="input-username"
                                placeholder="Username"
                                type="text"
                                readOnly
                              />
                            )}
                            <Button size="md" color="primary" onClick={() => setShowUsernameModal(true)}>
                              Edit
                            </Button>
                          </div>
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-email"
                          >
                            Email address
                          </label>
                          <Input
                            className="form-control-alternative"
                            defaultValue={user.email || ""}
                            id="input-email"
                            placeholder="Email"
                            type="email"
                            readOnly
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-first-name"
                          >
                            First name
                          </label>
                          <Input
                            className="form-control-alternative"
                            defaultValue={idTokenClaims?.given_name || ""}
                            id="input-first-name"
                            placeholder="First name"
                            type="text"
                            readOnly
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-last-name"
                          >
                            Last name
                          </label>
                          <Input
                            className="form-control-alternative"
                            defaultValue={idTokenClaims?.family_name || ""}
                            id="input-last-name"
                            placeholder="Last name"
                            type="text"
                            readOnly
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </div>
                  <hr className="my-4" />
                  {/* Additional fields */}
                  
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <AuthFooter />
      {showUsernameModal && (
        <UsernameModal
        onSuccess={async () => {
          // Trigger reauthentication (forces the user to log in again)
          const returnTo = location.pathname + location.search;
          localStorage.setItem("redirectUrl", returnTo);
          await loginWithRedirect({
            prompt: 'login', // Forces the user to log in again
          });
    
          // Close the modal after initiating reauthentication
          setShowUsernameModal(false);
        }}
      />
      )}
    </>
  );
};

export default Profile;
